/**
 * AccordionComponent - 手风琴组件
 * 支持多实例，每个实例独立管理自己的 item 状态
 * 支持PC端手风琴和移动端轮播图
 */
class AccordionComponent {
  constructor(container) {
    this.container = container;
    this.items = Array.from(container.querySelectorAll('.item'));
    this.isAnimating = false;
    this.animationTimer = null;
    this.isMobile = this.checkMobile();

    // 移动端轮播图相关属性（初始化时为空，动态创建后赋值）
    this.carouselTrack = null;
    this.carouselItems = [];
    this.currentIndex = 0;
    this.isTouch = false;
    this.startX = 0;
    this.currentX = 0;
    this.startTransform = 0;

    this.init();
  }

  /**
   * 检测是否为移动端
   */
  checkMobile() {
    return window.innerWidth <= 768;
  }

  /**
   * 初始化组件
   */
  init() {
    if (this.isMobile) {
      this.initMobileCarousel();
    } else {
      this.initDesktopAccordion();
    }

    this.bindEvents();

    // 监听窗口大小变化
    window.addEventListener('resize', () => {
      const newIsMobile = this.checkMobile();
      if (newIsMobile !== this.isMobile) {
        this.isMobile = newIsMobile;
        // 清除之前的事件监听器
        this.clearEvents();
        this.init();
      }
    });
  }

  /**
   * 清除事件监听器（避免重复绑定）
   */
  clearEvents() {
    // 这里可以添加清除事件的逻辑，目前使用简单的重新绑定方式
  }

  /**
   * 初始化桌面端手风琴
   */
  initDesktopAccordion() {
    const activeItem = this.container.querySelector('.item.active');
    if (!activeItem && this.items.length > 0) {
      this.items[0].classList.add('active');
    }
  }

  /**
   * 从PC端提取数据
   */
  extractDataFromDesktop() {
    const items = this.container.querySelectorAll('.desktop-accordion .item');
    const data = [];

    items.forEach((item, index) => {
      const activeImg = item.querySelector('.item-img .img.active');
      const title = item.querySelector('.item-info .title');
      const content = item.querySelector('.item-info .content');

      if (activeImg && title && content) {
        data.push({
          index: index,
          imgSrc: activeImg.src,
          imgAlt: activeImg.alt,
          title: title.textContent.trim(),
          content: content.textContent.trim()
        });
      }
    });

    return data;
  }

  /**
   * 创建移动端HTML结构
   */
  createMobileHTML(data) {
    const carouselItems = data.map((item, index) => `
      <div class="carousel-item ${index === 0 ? 'active' : ''}" data-index="${item.index}">
        <div class="carousel-img">
          <img src="${item.imgSrc}" alt="${item.imgAlt}" width="800" height="400" />
        </div>
        <div class="carousel-info">
          <h3 class="title">${item.title}</h3>
          <div class="content">${item.content}</div>
        </div>
      </div>
    `).join('');

    return `
      <div class="carousel-container">
        <div class="carousel-track">
          ${carouselItems}
        </div>
      </div>
    `;
  }

  /**
   * 插入移动端轮播图HTML
   */
  insertMobileCarousel() {
    const mobileCarousel = this.container.querySelector('.mobile-carousel');
    if (!mobileCarousel) return false;

    const data = this.extractDataFromDesktop();
    if (data.length === 0) return false;

    const html = this.createMobileHTML(data);
    mobileCarousel.innerHTML = html;

    // 重新获取移动端元素引用
    this.carouselTrack = this.container.querySelector('.carousel-track');
    this.carouselItems = Array.from(this.container.querySelectorAll('.carousel-item'));

    return true;
  }

  /**
   * 初始化移动端轮播图
   */
  initMobileCarousel() {
    // 先创建HTML结构
    if (!this.insertMobileCarousel()) {
      console.warn('Failed to create mobile carousel HTML');
      return;
    }

    this.currentIndex = 0;
    this.updateCarouselPosition();
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    if (this.isMobile && this.carouselTrack) {
      this.bindMobileEvents();
    } else {
      this.bindDesktopEvents();
    }
  }

  /**
   * 绑定桌面端事件
   */
  bindDesktopEvents() {
    // 为每个 item 绑定 hover 事件
    this.items.forEach(item => {
      item.addEventListener('mouseenter', () => {
        // 移除 isAnimating 的阻止逻辑，允许在动画过程中切换
        this.setActiveItem(item);
      });
    });
  }

  /**
   * 绑定移动端事件
   */
  bindMobileEvents() {
    if (!this.carouselTrack) return;

    // 触摸开始
    this.carouselTrack.addEventListener('touchstart', (e) => {
      this.isTouch = true;
      this.startX = e.touches[0].clientX;
      this.startTransform = this.getCurrentTransform();
      this.carouselTrack.style.transition = 'none';
    }, { passive: true });

    // 触摸移动
    this.carouselTrack.addEventListener('touchmove', (e) => {
      if (!this.isTouch) return;

      this.currentX = e.touches[0].clientX;
      const deltaX = this.currentX - this.startX;
      const newTransform = this.startTransform + deltaX;

      this.carouselTrack.style.transform = `translateX(${newTransform}px)`;
    }, { passive: true });

    // 触摸结束
    this.carouselTrack.addEventListener('touchend', () => {
      if (!this.isTouch) return;

      this.isTouch = false;
      this.carouselTrack.style.transition = 'transform 0.3s ease-out';

      const deltaX = this.currentX - this.startX;
      const threshold = 50; // 滑动阈值

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          this.goToPrevious();
        } else {
          this.goToNext();
        }
      } else {
        this.updateCarouselPosition();
      }
    }, { passive: true });
  }

  /**
   * 获取当前transform值
   */
  getCurrentTransform() {
    const style = window.getComputedStyle(this.carouselTrack);
    const matrix = style.transform;
    if (matrix === 'none') return 0;

    const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');
    return parseFloat(matrixValues[4]) || 0;
  }

  /**
   * 移动到下一个
   */
  goToNext() {
    if (this.currentIndex < this.carouselItems.length - 1) {
      this.currentIndex++;
      this.updateCarouselPosition();
    } else {
      this.updateCarouselPosition();
    }
  }

  /**
   * 移动到上一个
   */
  goToPrevious() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarouselPosition();
    } else {
      this.updateCarouselPosition();
    }
  }

  /**
   * 更新轮播图位置
   */
  updateCarouselPosition() {
    if (!this.carouselTrack || this.carouselItems.length === 0) return;

    const itemWidth = window.innerWidth - 48; // calc(100vw - 48px)
    const gap = 8;
    const offset = this.currentIndex * (itemWidth + gap);

    this.carouselTrack.style.transform = `translateX(-${offset}px)`;

    // 更新active状态
    this.carouselItems.forEach((item, index) => {
      if (index === this.currentIndex) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }

  /**
   * 设置激活的 item
   * @param {HTMLElement} targetItem - 要激活的 item
   */
  setActiveItem(targetItem) {
    if (targetItem.classList.contains('active')) {
      return;
    }

    // 清除之前的定时器（如果存在）
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }

    this.isAnimating = true;

    this.items.forEach(item => {
      item.classList.remove('active');
    });

    targetItem.classList.add('active');

    this.animationTimer = setTimeout(() => {
      this.isAnimating = false;
    }, 600);
  }
}

/**
 * 初始化所有手风琴组件
 */
function initAccordionComponents() {
  // 查找页面中所有的手风琴容器
  const accordionContainers = document.querySelectorAll('.accordion-component-box');
  
  if (accordionContainers.length === 0) {
    console.warn('No accordion components found on this page.');
    return;
  }
  
  // 为每个容器创建独立的组件实例
  accordionContainers.forEach((container, index) => {
    try {
      new AccordionComponent(container);
      console.log(`Accordion component ${index + 1} initialized successfully.`);
    } catch (error) {
      console.error(`Failed to initialize accordion component ${index + 1}:`, error);
    }
  });
}

// DOM 加载完成后初始化组件
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccordionComponents);
} else {
  // 如果 DOM 已经加载完成，直接执行初始化
  initAccordionComponents();
}